import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSavedProcedures } from '../../context/SavedProceduresContext';

const SaveProcedureButton = ({ noticeNo, variant = 'default' }) => {
  const { isAuthenticated, hasActiveSubscription } = useAuth();
  const { isSaved, toggleSave, loaded } = useSavedProcedures();
  const [loading, setLoading] = useState(false);

  // Defensive: if the caller passes null/undefined/'null'/'undefined'/empty/'N/A',
  // we have nothing to save — the backend would get /api/SavedProcedures/null.
  const hasValidNoticeNo =
    typeof noticeNo === 'string' &&
    noticeNo.trim() !== '' &&
    noticeNo !== 'null' &&
    noticeNo !== 'undefined' &&
    noticeNo !== 'N/A';

  const saved = hasValidNoticeNo && isSaved(noticeNo);

  const navigate = useNavigate();

  const handleToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    if (!hasActiveSubscription) {
      navigate('/abonamente');
      return;
    }

    // Should never happen because of the guard below that hides the button,
    // but belt-and-suspenders: bail out before a bad POST.
    if (!hasValidNoticeNo) return;

    if (loading) return;
    setLoading(true);
    await toggleSave(noticeNo);
    setLoading(false);
  };

  // Hide completely for users without subscription or when we don't have a valid noticeNo.
  if (!hasActiveSubscription) return null;
  if (!loaded && isAuthenticated) return null;
  if (!hasValidNoticeNo) return null;

  const isCompact = variant === 'compact';

  const savedClass = isCompact
    ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all bg-green-50 text-green-700 border-green-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 shadow-sm'
    : 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all bg-green-50 text-green-700 border-green-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300 shadow-md';

  const unsavedClass = isCompact
    ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border-amber-300 hover:from-amber-100 hover:to-yellow-100 hover:border-amber-400 shadow-sm'
    : 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border-amber-300 hover:from-amber-100 hover:to-yellow-100 hover:border-amber-400 shadow-md hover:shadow-lg';

  const iconSize = isCompact ? 14 : 18;

  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`${saved ? savedClass : unsavedClass} disabled:opacity-50 disabled:cursor-not-allowed`}
        title={
          !isAuthenticated
            ? 'Autentifica-te pentru a salva proceduri'
            : saved
              ? 'Elimina din procedurile salvate'
              : 'Salveaza procedura'
        }
      >
        {loading ? (
          <Loader2 size={iconSize} className="animate-spin" />
        ) : saved ? (
          <BookmarkCheck size={iconSize} />
        ) : (
          <Bookmark size={iconSize} />
        )}
        {saved ? 'Salvata' : 'Salveaza'}
      </button>

    </div>
  );
};

export default SaveProcedureButton;
